import React, { createContext, useState, useEffect, useContext } from 'react';
import { AuthContext } from '../context/AuthContext';
import axios from 'axios';

export const OrderContext = createContext();

export const OrderProvider = ({ children }) => {
  const { user } = useContext(AuthContext);
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    // Funzione per ottenere gli ordini dal server
    const fetchOrders = async () => {
      const token = localStorage.getItem('token');
      if (user && token) {
        try {
          const response = await axios.get('/api/orders', {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
          console.log('Ordini ricevuti dal server:', response.data);
          setOrders(response.data);
          //console.log("Ordini caricati nel contesto:", response.data);
        } catch (error) {
          console.error('Errore durante il recupero degli ordini:', error.response ? error.response.data : error.message);
          if (error.response) {
            console.error('Response data:', error.response.data);
            console.error('Response status:', error.response.status);
            console.error('Response headers:', error.response.headers);
          }
        }
      } else {
        console.error('Utente non autenticato o token mancante.');
      }
    };

    if (user != null) {
      fetchOrders();
    }
  }, [user]);

  // Funzione per salvare un nuovo ordine
  const placeOrder = async (order) => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        console.log('Tentativo di creare un ordine con token:', token, 'e dati ordine:', order);
        const response = await axios.post('/api/orders', {
          ...order,
          userId: user.id,
        }, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        console.log('Ordine salvato con successo:', response.data);
        setOrders([...orders, response.data]);
      } catch (error) {
        console.error('Errore durante il salvataggio dell\'ordine:', error.response ? error.response.data : error.message);
      }
    }
  };

  // Funzione per eliminare un ordine
  const deleteOrder = async (id) => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        await axios.delete(`/api/orders/${id.toString()}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setOrders(orders.filter(order => order._id.toString() !== id.toString()));
      } catch (error) {
        console.error('Errore durante l\'eliminazione dell\'ordine:', error.response ? error.response.data : error.message);
      }
    }
  };

  // Funzione per aggiornare un ordine esistente
  const updateOrder = async (updatedOrder) => {
    const token = localStorage.getItem('token');
    if (token && updatedOrder._id) {
      try {
        console.log('Tentativo di aggiornare un ordine con id:', updatedOrder._id);
        const response = await axios.put(`/api/orders/${String(updatedOrder._id)}`, updatedOrder, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        console.log('Ordine aggiornato con successo:', response.data);
        setOrders(orders.map(order => order._id === updatedOrder._id ? response.data : order));
      } catch (error) {
        console.error('Errore durante l\'aggiornamento dell\'ordine:', error.response ? error.response.data : error.message);
      }
    } else {
      console.error('ID ordine non valido o token mancante');
    }
  };

    // Funzione per aggiornare SOLO il destinatario (recipient) di un ordine
  const updateOrderRecipient = async (orderId, recipient) => {
    const token = localStorage.getItem('token');
    if (token && orderId) {
      try {
        console.log('Aggiornamento destinatario ordine:', orderId, recipient);
        const response = await axios.patch(
          `/api/orders/${String(orderId)}/recipient`,
          { recipient },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        // Aggiorna anche lo stato locale degli ordini
        setOrders(prev =>
          prev.map(o => (o._id === orderId ? response.data : o))
        );
      } catch (error) {
        console.error('Errore durante l\'aggiornamento del destinatario:', error.response ? error.response.data : error.message);
        throw error;
      }
    } else {
      console.error('ID ordine non valido o token mancante per updateOrderRecipient');
    }
  };


  // Funzione per inviare un ordine. pdfBase64 (opzionale): PDF del preventivo già generato
  // lato client, da inoltrare al destinatario se ha un'email registrata.
  const sendOrder = async (orderId, pdfBase64) => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        console.log('Tentativo di inviare l\'ordine con id:', orderId);
        const response = await axios.post(`/api/orders/${orderId}/send`, { pdfBase64 }, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        console.log('Ordine inviato con successo:', response.data);
        setOrders(orders.map(order => order._id === orderId ? { ...order, status: 'inviato' } : order));
        return response.data;
      } catch (error) {
        console.error('Errore durante l\'invio dell\'ordine:', error.response ? error.response.data : error.message);
        throw error;
      }
    }
  };

  const requestOrderEdit = async (orderId, reason) => {
    const token = localStorage.getItem('token');
    if (!token) throw new Error('Token mancante');
    const response = await axios.post(
      `/api/orders/${orderId}/request-edit`,
      { reason },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    const updated = response.data?.order;
    if (updated?._id) {
      setOrders((prev) => prev.map((o) => (o._id === updated._id ? updated : o)));
    }
    return updated;
  };

  // L'agente segnala che il cliente ha confermato il preventivo: solo da qui GOOOLED viene notificata.
  const confirmCustomerOrder = async (orderId) => {
    const token = localStorage.getItem('token');
    if (!token) throw new Error('Token mancante');
    const response = await axios.put(
      `/api/orders/${orderId}/confirm-customer`,
      {},
      { headers: { Authorization: `Bearer ${token}` } }
    );
    const updated = response.data?.order;
    if (updated?._id) {
      setOrders((prev) => prev.map((o) => (o._id === updated._id ? updated : o)));
    }
    return updated;
  };

  const markOrderAsReceived = async (orderId) => {
    const token = localStorage.getItem('token');
    if (!token) throw new Error('Token mancante');
    const response = await axios.put(
      `/api/orders/${orderId}/receive`,
      {},
      { headers: { Authorization: `Bearer ${token}` } }
    );
    const updated = response.data?.order;
    if (updated?._id) {
      setOrders((prev) => prev.map((o) => (o._id === updated._id ? updated : o)));
    }
    return updated;
  };

  const rejectOrder = async (orderId, reason) => {
    const token = localStorage.getItem('token');
    if (!token) throw new Error('Token mancante');
    const response = await axios.put(
      `/api/orders/${orderId}/reject`,
      { reason },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    const updated = response.data?.order;
    if (updated?._id) {
      setOrders((prev) => prev.map((o) => (o._id === updated._id ? updated : o)));
    }
    return updated;
  };

  const approveOrderEditRequest = async (orderId, note = '') => {
    const token = localStorage.getItem('token');
    if (!token) throw new Error('Token mancante');
    const response = await axios.put(
      `/api/orders/${orderId}/edit-request/approve`,
      { note },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    const updated = response.data?.order;
    if (updated?._id) {
      setOrders((prev) => prev.map((o) => (o._id === updated._id ? updated : o)));
    }
    return updated;
  };

  const rejectOrderEditRequest = async (orderId, note = '') => {
    const token = localStorage.getItem('token');
    if (!token) throw new Error('Token mancante');
    const response = await axios.put(
      `/api/orders/${orderId}/edit-request/reject`,
      { note },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    const updated = response.data?.order;
    if (updated?._id) {
      setOrders((prev) => prev.map((o) => (o._id === updated._id ? updated : o)));
    }
    return updated;
  };

  const clearOldOrders = () => {
    const now = Date.now();
    const ninetyDays = 90 * 24 * 60 * 60 * 1000;
    setOrders((prev) => prev.filter((o) => {
      const ts = new Date(o?.date).getTime();
      return Number.isFinite(ts) ? now - ts <= ninetyDays : true;
    }));
  };

  /*const loadOrderIntoCart = (order, addToCart, clearCart) => {
    clearCart();
    order.items.forEach(item => addToCart(item));
  };*/

    return (
    <OrderContext.Provider
      value={{
        orders,
        setOrders,
        placeOrder,
        deleteOrder,
        updateOrder,
        updateOrderRecipient,
        sendOrder, /* loadOrderIntoCart se lo riattivi */
        confirmCustomerOrder,
        requestOrderEdit,
        markOrderAsReceived,
        rejectOrder,
        approveOrderEditRequest,
        rejectOrderEditRequest,
        clearOldOrders,
      }}
    >
      {children}
    </OrderContext.Provider>
  );
};

